import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Container,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  Box,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle,
  Zap,
  Users,
  Headphones,
  Shield,
  TrendingUp,
  Sparkles,
  Rocket,
  BarChart3,
  Lock
} from 'lucide-react';
import { API_BASE } from '../config';

const plans = [
  {
    name: 'Starter',
    monthlyPrice: 499,
    yearlyPrice: 5388,
    description: 'Perfect for small projects and MVPs',
    icon: Sparkles,
    popular: false,
    badge: 'Best for Startups',
    features: [
      { text: 'Basic AI model', icon: Sparkles },
      { text: '1 month support', icon: Headphones },
      { text: 'Up to 1,000 API calls', icon: Zap },
      { text: 'Basic analytics', icon: BarChart3 },
      { text: 'Community support', icon: Users },
      { text: 'Standard security', icon: Lock }
    ],
    platforms: [
      { name: 'Open Source', tech: ['Python', 'Node.js', 'React', 'Vue.js'] },
      { name: 'Testing Frameworks', tech: ['Selenium', 'JUnit', 'Pytest'] },
      { name: 'Databases', tech: ['PostgreSQL', 'MySQL', 'MongoDB'] },
      { name: 'AI/ML', tech: ['TensorFlow', 'Scikit-learn', 'Basic Analytics'] }
    ],
    limitations: ['Limited model training', 'No custom integrations']
  },
  {
    name: 'Business',
    monthlyPrice: 1999,
    yearlyPrice: 22788,
    description: 'Ideal for growing companies',
    icon: Rocket,
    popular: true,
    badge: 'Most Popular',
    features: [
      { text: 'Custom AI model', icon: Sparkles },
      { text: '3 months support', icon: Headphones },
      { text: 'Up to 50,000 API calls', icon: Zap },
      { text: 'Advanced analytics', icon: BarChart3 },
      { text: 'Priority support', icon: Users },
      { text: 'Enterprise security', icon: Lock }
    ],
    platforms: [
      { name: 'Open Source', tech: ['Python', 'Node.js', 'React', 'Vue.js', 'Angular'] },
      { name: 'Java Enterprise', tech: ['Spring Boot', 'Spring Cloud', 'Microservices'] },
      { name: '.NET', tech: ['.NET Core', 'ASP.NET', 'Entity Framework'] },
      { name: 'Testing Frameworks', tech: ['Selenium', 'JUnit', 'TestNG', 'Cucumber', 'Appium'] },
      { name: 'Messaging', tech: ['Kafka', 'RabbitMQ', 'ActiveMQ'] },
      { name: 'Databases', tech: ['PostgreSQL', 'MySQL', 'MongoDB', 'Oracle DB', 'SQL Server'] },
      { name: 'AI/ML', tech: ['TensorFlow', 'PyTorch', 'Advanced Analytics', 'Custom Models'] }
    ],
    bonusFeatures: ['Model fine-tuning', 'Dedicated manager', 'Multi-platform support']
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    yearlyPrice: null,
    description: 'Complete solution for enterprises',
    icon: TrendingUp,
    popular: false,
    badge: 'For Large Scale',
    features: [
      { text: 'Full AI solution', icon: Sparkles },
      { text: 'Priority support (24/7)', icon: Headphones },
      { text: 'Unlimited API calls', icon: Zap },
      { text: 'Real-time analytics', icon: BarChart3 },
      { text: 'Dedicated team', icon: Users },
      { text: 'Advanced security', icon: Lock }
    ],
    platforms: [
      { name: 'Open Source', tech: ['Python', 'Node.js', 'React', 'Vue.js', 'Angular', 'Go', 'Rust'] },
      { name: 'Java Enterprise', tech: ['Spring Boot', 'Spring Cloud', 'Microservices', 'JEE', 'Kafka', 'Hadoop'] },
      { name: '.NET', tech: ['.NET Core', '.NET Framework', 'ASP.NET', 'Blazor', 'Entity Framework', 'LINQ'] },
      { name: 'Enterprise Systems', tech: ['Oracle', 'PeopleSoft', 'Kronos', 'SAP'] },
      { name: 'Middleware & Integration', tech: ['DataPower', 'MuleSoft', 'TIBCO', 'IBM Integration Bus'] },
      { name: 'Testing Frameworks', tech: ['Selenium', 'JUnit', 'TestNG', 'Cucumber', 'Appium', 'LoadRunner', 'JMeter'] },
      { name: 'Messaging & Streaming', tech: ['Kafka', 'RabbitMQ', 'ActiveMQ', 'Redis', 'AWS SQS'] },
      { name: 'Cloud Platforms', tech: ['AWS', 'Azure', 'GCP', 'Kubernetes', 'Docker'] },
      { name: 'Databases', tech: ['Oracle DB', 'SQL Server', 'PostgreSQL', 'MySQL', 'MongoDB', 'Cassandra', 'Snowflake'] },
      { name: 'AI/ML', tech: ['TensorFlow', 'PyTorch', 'Advanced Analytics', 'Custom Models', 'NLP', 'Computer Vision', 'Real-time ML'] }
    ],
    bonusFeatures: ['Custom integrations', 'White-label options', 'SLA guarantee', 'On-premise deployment', 'Multi-platform support', 'Dedicated infrastructure']
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  hover: {
    y: -10,
    transition: {
      duration: 0.3,
    },
  },
};

export default function Pricing() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const [billingCycle, setBillingCycle] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChoosePlan = (plan) => {
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setForm({ name: '', email: '', company: '', phone: '', message: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      setSnackbar({ open: true, severity: 'error', message: 'Please fill in name and email' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/pricing/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planName: selectedPlan.name,
          price: selectedPlan.price,
          name: form.name,
          email: form.email,
          company: form.company,
          phone: form.phone,
          message: form.message
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: `🎉 ${selectedPlan.name} plan selected! We'll contact you soon.`
        });
        handleDialogClose();
        setSelectedPlan(null);
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: data.message || 'Failed to select plan. Please try again.'
        });
      }
    } catch (err) {
      console.error('Error selecting plan:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Error submitting plan selection. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      py: { xs: 6, md: 12 }, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 50%, rgba(102,126,234,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118,75,162,0.05) 0%, transparent 50%)',
        pointerEvents: 'none'
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Chip
                label="💎 AI-Powered Pricing"
                sx={{ 
                  mb: 3, 
                  fontSize: '0.95rem', 
                  py: 3, 
                  px: 3,
                  background: 'white',
                  backdropFilter: 'blur(10px)',
                  color: '#667eea',
                  border: '1px solid rgba(102,126,234,0.2)',
                  fontWeight: 700,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                }}
              />
            </motion.div>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              sx={{ 
                mb: 4, 
                fontSize: { xs: '1rem', md: '1.2rem' }, 
                maxWidth: 600, 
                mx: 'auto',
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              Choose the perfect AI solution for your business needs. Scale from startup to enterprise with our flexible plans.
            </Typography>

            {/* Billing Cycle Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 6, flexWrap: 'wrap' }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                background: 'white', 
                backdropFilter: 'blur(10px)',
                p: 0.5, 
                borderRadius: 3, 
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                border: '1px solid rgba(102,126,234,0.1)'
              }}>
                <Button
                  variant={billingCycle === 0 ? 'contained' : 'text'}
                  onClick={() => setBillingCycle(0)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    background: billingCycle === 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: billingCycle === 0 ? 'white' : '#667eea',
                    '&:hover': {
                      background: billingCycle === 0 ? 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' : 'rgba(102,126,234,0.05)'
                    }
                  }}
                >
                  Monthly
                </Button>
                <Button
                  variant={billingCycle === 1 ? 'contained' : 'text'}
                  onClick={() => setBillingCycle(1)}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    background: billingCycle === 1 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                    color: billingCycle === 1 ? 'white' : '#667eea',
                    '&:hover': {
                      background: billingCycle === 1 ? 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' : 'rgba(102,126,234,0.05)'
                    }
                  }}
                >
                  Yearly (Save 20%)
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid 
            container 
            spacing={3} 
            sx={{ 
              mb: 8,
              display: 'flex',
              alignItems: 'stretch'
            }}
          >
            {plans.map((plan, index) => {
              const PlanIcon = plan.icon;
              const isPopular = plan.popular;

              return (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch'
                  }}
                >
                  <motion.div 
                    variants={cardVariants} 
                    whileHover="hover"
                    style={{ width: '100%', display: 'flex' }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        background: isPopular
                          ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)'
                          : 'rgba(255,255,255,0.9)',
                        backdropFilter: 'blur(20px)',
                        color: 'inherit',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isPopular ? '0 20px 60px rgba(102, 126, 234, 0.4)' : '0 10px 40px rgba(0,0,0,0.15)',
                        border: isPopular ? '2px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(255,255,255,0.5)',
                        borderRadius: 4,
                        '&:hover': {
                          boxShadow: isPopular
                            ? '0 30px 80px rgba(102, 126, 234, 0.5)'
                            : '0 20px 60px rgba(0,0,0,0.2)',
                          transform: 'translateY(-12px) scale(1.02)',
                          border: isPopular ? '2px solid rgba(102, 126, 234, 0.5)' : '1px solid rgba(102, 126, 234, 0.3)'
                        },
                        overflow: 'visible'
                      }}
                    >
                      {isPopular && (
                        <Badge
                          badgeContent={plan.badge}
                          sx={{
                            position: 'absolute',
                            top: -15,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            '& .MuiBadge-badge': {
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              padding: '8px 20px',
                              fontSize: '0.8rem',
                              fontWeight: 800,
                              borderRadius: 30,
                              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                              border: '2px solid white'
                            }
                          }}
                        />
                      )}

                      <CardHeader
                        avatar={<PlanIcon size={36} style={{ color: '#667eea' }} />}
                        title={
                          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#667eea' }}>
                            {plan.name}
                          </Typography>
                        }
                        subheader={
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {plan.description}
                          </Typography>
                        }
                        sx={{ pb: 2 }}
                      />

                      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Price Section */}
                        <Box sx={{ mb: 3, pb: 3, borderBottom: '2px solid rgba(102, 126, 234, 0.1)' }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 900,
                              mb: 0.5,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            {plan.monthlyPrice !== null ? (
                              <>
                                ${billingCycle === 0 ? plan.monthlyPrice.toLocaleString() : plan.yearlyPrice.toLocaleString()}
                              </>
                            ) : (
                              'Custom'
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {plan.monthlyPrice !== null ? (
                              billingCycle === 0 ? '/month' : '/year (Save 20%)'
                            ) : (
                              'Based on needs'
                            )}
                          </Typography>
                        </Box>

                        {/* Features List */}
                        <List sx={{ flex: 1, py: 0 }}>
                          {plan.features.map((feature, idx) => {
                            const FeatureIcon = feature.icon;
                            return (
                              <ListItem
                                key={idx}
                                sx={{
                                  py: 1.5,
                                  px: 0
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36, color: '#667eea' }}>
                                  <FeatureIcon size={20} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={feature.text}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: { fontWeight: 600, color: 'text.primary' }
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>

                        {/* Supported Platforms & Technologies */}
                        {plan.platforms && (
                          <Box sx={{ mt: 3, pt: 3, borderTop: '2px solid rgba(102, 126, 234, 0.1)' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: '#667eea',
                                display: 'block',
                                mb: 2,
                                fontSize: '0.75rem'
                              }}
                            >
                              📚 Supported Technologies
                            </Typography>
                            <Stack spacing={2}>
                              {plan.platforms.map((platform, pidx) => (
                                <Box key={pidx}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      color: '#667eea',
                                      display: 'block',
                                      mb: 0.8,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {platform.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                    {platform.tech.map((tech, tidx) => (
                                      <Chip
                                        key={tidx}
                                        label={tech}
                                        size="small"
                                        sx={{
                                          height: 24,
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          background: 'rgba(102, 126, 234, 0.1)',
                                          color: '#667eea',
                                          border: '1px solid rgba(102, 126, 234, 0.2)',
                                          '&:hover': {
                                            background: 'rgba(102, 126, 234, 0.2)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
                                          },
                                          transition: 'all 0.2s ease'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Bonus Features */}
                        {plan.bonusFeatures && (
                          <Box sx={{ mt: 2, pt: 3, borderTop: '2px solid rgba(102, 126, 234, 0.1)' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                color: '#667eea',
                                fontSize: '0.75rem'
                              }}
                            >
                              ✨ Bonus Features
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 1.5 }}>
                              {plan.bonusFeatures.map((feature, idx) => (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: '#667eea'
                                  }}
                                >
                                  <CheckCircle size={16} />
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {feature}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>

                      <Divider sx={{ my: 2, bgcolor: 'rgba(102, 126, 234, 0.1)' }} />

                      <Box sx={{ p: 3 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => handleChoosePlan(plan)}
                          sx={{
                            py: 2,
                            fontWeight: 700,
                            fontSize: '1rem',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                              boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)',
                              transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Get Started →
                        </Button>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </motion.div>

        {/* FAQ or CTA Section */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }}>
          <Card
            sx={{
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.5)',
              p: 5,
              textAlign: 'center',
              borderRadius: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
            }}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '50%',
                  p: 2,
                  display: 'inline-flex',
                  boxShadow: '0 8px 30px rgba(102, 126, 234, 0.3)'
                }}>
                  <Shield size={40} color="white" />
                </Box>
              </Box>
            </motion.div>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#667eea' }}>
              Not sure which plan is right for you?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto', fontSize: '1.05rem' }}>
              Our team is here to help! Get a personalized recommendation and discuss your specific requirements with our AI experts.
            </Typography>
            <Button
              variant="contained"
              sx={{ 
                py: 2, 
                px: 5, 
                fontWeight: 700,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                boxShadow: '0 8px 30px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
              href="/contact"
            >
              Schedule a Free Consultation
            </Button>
          </Card>
        </motion.div>
      </Container>

      {/* Plan Selection Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', fontWeight: 800 }}>
          ✨ Complete Your Selection: {selectedPlan?.name} Plan
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Full Name"
              name="name"
              value={form.name}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="Enter your name"
              variant="outlined"
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="Enter your email"
              variant="outlined"
            />
            <TextField
              label="Company"
              name="company"
              value={form.company}
              onChange={handleInputChange}
              fullWidth
              placeholder="Your company name (optional)"
              variant="outlined"
            />
            <TextField
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={handleInputChange}
              fullWidth
              placeholder="Your phone number (optional)"
              variant="outlined"
            />
            <TextField
              label="Tell us about your project"
              name="message"
              value={form.message}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={4}
              placeholder="Describe your AI needs, timeline, and goals..."
              variant="outlined"
            />
            <Card
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 2,
                mt: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Sparkles size={20} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Selected Plan
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                {selectedPlan?.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Investment: ${selectedPlan?.monthlyPrice !== null ? (billingCycle === 0 ? selectedPlan?.monthlyPrice : selectedPlan?.yearlyPrice) : 'Custom'} {selectedPlan?.monthlyPrice !== null ? (billingCycle === 0 ? '/month' : '/year') : ''}
              </Typography>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={handleDialogClose} disabled={submitting} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontWeight: 700
            }}
          >
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Start Free Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            fontWeight: 600,
            fontSize: '0.95rem',
            borderRadius: 2
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
